import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, X, FolderOpen, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { obtenerToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Buscador de expedientes reutilizable para los módulos de análisis IA.
 * Devuelve el expediente elegido vía onChange({id, numero_expediente, titulo}) o null.
 */
export const ExpedienteSelector = ({ value, onChange, placeholder = 'Buscar expediente (nº o título)…', testId = 'expediente-selector' }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!term || term.length < 2) { setResults([]); return; }
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API}/api/hipotecas/expedientes-buscar?q=${encodeURIComponent(term)}&limit=15`,
          { headers: { Authorization: `Bearer ${obtenerToken()}` } },
        );
        if (!cancel) { setResults(r.data || []); setOpen(true); }
      } catch (e) {
        if (!cancel) setResults([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 350);
    return () => { cancel = true; clearTimeout(t); };
  }, [term]);

  if (value) {
    return (
      <div
        className="flex items-center gap-2 h-10 px-3 rounded-md border border-emerald-300 bg-emerald-50 text-sm min-w-[260px]"
        data-testid={`${testId}-selected`}
      >
        <FolderOpen className="w-4 h-4 text-emerald-600 shrink-0" />
        <span className="truncate font-medium text-emerald-900" title={value.titulo}>
          {value.numero_expediente}
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-auto text-emerald-700 hover:text-red-600 transition-colors"
          data-testid={`${testId}-clear`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative min-w-[260px]" ref={boxRef}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <Input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder={placeholder}
        className="pl-9"
        data-testid={`${testId}-input`}
      />
      {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
      {open && results.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-md border bg-white shadow-lg"
          data-testid={`${testId}-results`}
        >
          {results.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => { onChange(e); setOpen(false); setTerm(''); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0 transition-colors"
              data-testid={`${testId}-option-${e.id}`}
            >
              <div className="text-sm font-medium text-gray-900">{e.numero_expediente}</div>
              <div className="text-xs text-gray-500 break-words">{e.titulo}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExpedienteSelector;
